import type { IMetaObjectTypeQuery } from "@repo/shopify/queries/metaobject-gql";
import { metaobjectTypeQuery } from "@repo/shopify/queries/metaobject-gql";
import { gql, useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ICustomerQuery } from "@repo/shopify/queries/customer-gql";
import type { ICart } from "@repo/shopify/queries/cart-gql";
import type {
  Maybe,
  Metaobject,
  Product,
} from "@shopify/hydrogen-react/storefront-api-types";

interface IGift {
  addCartLines: { merchandiseId: string; quantity: number }[];
  removeCartLines: string[];
}

const GIFT_TAGS = new Set(["Gift", "GWP"]);

export default function useGift({
  cart,
  customer,
}: {
  cart?: ICart["cart"];
  customer: ICustomerQuery["customer"];
}) {
  const [giftByPrice, setGiftByPrice] = useState<IMetaObjectTypeQuery>();
  const [giftByProduct, setGiftByProduct] = useState<IMetaObjectTypeQuery>();
  const [result, setResult] = useState<IGift>({
    addCartLines: [],
    removeCartLines: [],
  });

  const [fetchMetaObjectType] = useLazyQuery<IMetaObjectTypeQuery>(
    gql(metaobjectTypeQuery.query),
    {
      variables: { first: 30, reverse: true },
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    void handleGifts();
  }, []);

  const handleGifts = async () => {
    const { data: priceData } = await fetchMetaObjectType({
      variables: { type: "gift_by_price" },
    });
    const { data: productData } = await fetchMetaObjectType({
      variables: { type: "gift_by_product" },
    });

    setGiftByPrice(priceData);
    setGiftByProduct(productData);
  };

  // 고객의 과거 주문 기록 평탄화
  const orders = useMemo(
    () => customer.orders.edges.flatMap((edge) => edge.node.lineItems.edges),
    [customer],
  );

  // 현재 카트에 있는 사은품들
  const giftLines = useMemo(
    () =>
      cart?.lines.edges.filter(({ node }) =>
        node.merchandise.product.tags.some((tag) => GIFT_TAGS.has(tag)),
      ),
    [cart],
  );

  // 2. 사은품 유효성 검사 로직 (공통)
  const filterAvailableGift = useCallback(
    (gift: Metaobject) => {
      const giftItem = gift.fields.find((obj) => obj.key === "gift_product")
        ?.reference as Maybe<Product>;
      if (!giftItem) return false;

      // 사은품이 유효한지 확인
      if (!giftItem.availableForSale) return false;

      // 사은품 갯수가 있는지 확인
      if ((giftItem.totalInventory ?? 0) <= 0) return false;

      // 사은품 옵션이 있는지 확인
      if (!giftItem.variants.edges.length) return false;

      const now = new Date().getTime();

      // 기간 확인
      const startValue = gift.fields.find(
        (obj) => obj.key === "start_datetime",
      )?.value;
      const endValue = gift.fields.find(
        (obj) => obj.key === "end_datetime",
      )?.value;
      const startTime = startValue ? new Date(startValue).getTime() : -1;
      const endTime = endValue ? new Date(endValue).getTime() : -1;

      if (startTime !== -1 && now < startTime) return false;
      if (endTime !== -1 && now > endTime) return false;

      // 인당 수량 제한 확인
      const quantityLimit = Number(
        gift.fields.find((obj) => obj.key === "quantity_limit")?.value || "-1",
      );
      if (quantityLimit !== -1) {
        const ordersWithGift = orders.filter(
          (lineItem) => lineItem.node.variant?.product.id === giftItem.id,
        );
        if (ordersWithGift.length >= quantityLimit) return false;
      }

      return true;
    },
    [orders],
  );

  // 3. 메인 계산 로직
  useEffect(() => {
    checkGift();
  }, [cart, giftByPrice, giftByProduct, giftLines]);

  const checkGift = () => {
    if (!giftByPrice || !giftByProduct) return;

    // 조건에 맞는 "Gift By Product" 찾기 (품절 항목 제외)
    const cartProductIds = cart?.lines.edges
      .filter(
        ({ node }) =>
          node.merchandise.availableForSale &&
          !node.merchandise.product.tags.some((tag) => GIFT_TAGS.has(tag)),
      )
      .map(({ node }) => node.merchandise.product.id);

    const targetProductGift = giftByProduct.metaobjects.edges
      .map(({ node }) => node)
      .filter(filterAvailableGift)
      .filter((gift) => {
        const conditionValue =
          gift.fields.find((item) => item.key === "condition_product")?.value ||
          "[]";
        const conditionIds = JSON.parse(conditionValue) as string[];
        return cartProductIds?.some((id) => conditionIds.includes(id));
      });

    // 조건에 맞는 "Gift By Price" 찾기 (금액 높은 순 정렬)
    const subTotalPrice = parseFloat(cart?.cost.subtotalAmount.amount || "0");

    // 사은품에 지정된 제외 상품(exclude_product) 금액을 뺀 카트 합계
    const getConditionPrice = (gift: Metaobject) => {
      const excludeValue =
        gift.fields.find((item) => item.key === "exclude_product")?.value ||
        "[]";
      const excludeIds = new Set(JSON.parse(excludeValue) as string[]);
      if (!excludeIds.size) return subTotalPrice;

      // 카트 합계(cost.subtotalAmount)는 라인 할인이 적용된 금액이므로 라인도 totalAmount 기준으로 뺀다
      const excludedPrice =
        cart?.lines.edges
          .filter(({ node }) => excludeIds.has(node.merchandise.product.id))
          .reduce(
            (sum, { node }) =>
              sum + parseFloat(node.cost.totalAmount.amount || "0"),
            0,
          ) || 0;

      return subTotalPrice - excludedPrice;
    };

    const targetPriceGift = giftByPrice.metaobjects.edges
      .map(({ node }) => node)
      .filter(filterAvailableGift)
      .sort((a, b) => {
        const priceA =
          Number(
            a.fields.find((item) => item.key === "price_condition")?.value,
          ) || 0;
        const priceB =
          Number(
            b.fields.find((item) => item.key === "price_condition")?.value,
          ) || 0;
        return priceB - priceA;
      })
      .find((gift) => {
        const condition =
          Number(
            gift.fields.find((item) => item.key === "price_condition")?.value,
          ) || 0;
        return getConditionPrice(gift) >= condition;
      });

    // 현재 있어야 할 사은품들의 Variant ID (중복 제거)
    const targetVariantIds = new Set<string>();

    targetProductGift.forEach((gift) => {
      const giftProduct = gift.fields.find(
        (item) => item.key === "gift_product",
      )?.reference as Product;
      const variantId = giftProduct.variants.edges.find(
        ({ node }) => node.availableForSale,
      );
      if (variantId) targetVariantIds.add(variantId.node.id);
    });

    if (targetPriceGift) {
      const giftProduct = targetPriceGift.fields.find(
        (item) => item.key === "gift_product",
      )?.reference as Product;
      const variantId = giftProduct.variants.edges.find(
        ({ node }) => node.availableForSale,
      );
      if (variantId) targetVariantIds.add(variantId.node.id);
    }

    // 삭제: 카트에 있는데 targetVariantIds에 없는 것
    const removeCartLines =
      giftLines
        ?.filter(({ node }) => !targetVariantIds.has(node.merchandise.id))
        .map(({ node }) => node.id) || [];

    // 추가: targetVariantIds에 있는데 카트에 없는 것
    const addCartLines = Array.from(targetVariantIds)
      .filter(
        (id) => !giftLines?.some(({ node }) => node.merchandise.id === id),
      )
      .map((id) => ({ merchandiseId: id, quantity: 1 }));

    setResult({ addCartLines, removeCartLines });
  };

  return result;
}
